// src/components/common/Footer.ts
import CookieManager from '../../utils/CookieManager';
import logoUrl from '../../assets/logo-final.png';

export default class Footer {
  private element: HTMLElement;

  constructor() {
    this.element = document.createElement('footer');
    this.element.className = 'relative bg-gradient-to-br from-won-black via-gray-900 to-won-dark text-white pt-20 pb-8 overflow-hidden';
    this.render();
    this.setupEventListeners();
    this.addBackgroundEffects();
  }

  private render(): void {
    this.element.innerHTML = `
      <!-- Style CSS pour forcer l'affichage correct du footer -->
      <style>
        footer h3 {
          border-left: none !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
        }
        footer ul {
          list-style: none !important;
          padding-left: 0 !important;
          margin-left: 0 !important;
        }
        footer ul li::before {
          display: none !important;
        }
        footer ul li::marker {
          display: none !important;
        }
        footer .footer-icon-container {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        footer .footer-title-container {
          display: flex !important;
          align-items: center !important;
        }
        footer .footer-title-container h3 {
          margin: 0 !important;
          line-height: 2rem !important;
        }
      </style>

      <!-- Effet de particules en arrière-plan -->
      <div class="absolute inset-0 opacity-10">
        <div class="absolute top-10 left-10 w-2 h-2 bg-won-pink rounded-full animate-pulse"></div>
        <div class="absolute top-32 right-20 w-1 h-1 bg-won-teal rounded-full animate-ping"></div>
        <div class="absolute bottom-20 left-1/4 w-1.5 h-1.5 bg-won-blue rounded-full animate-pulse" style="animation-delay: 1s;"></div>
        <div class="absolute top-20 left-1/3 w-1 h-1 bg-won-orange rounded-full animate-ping" style="animation-delay: 2s;"></div>
        <div class="absolute bottom-32 right-1/3 w-2 h-2 bg-won-emerald rounded-full animate-pulse" style="animation-delay: 3s;"></div>
      </div>

      <div class="container relative z-10">

        <div class="flex flex-col lg:flex-row justify-between items-start mb-10 border-b border-gray-700/50 pb-10">
          <div class="max-w-sm mb-12 lg:mb-0">
            <!-- Logo simple sans effet -->
            <div class="mb-6">
              <a href="index.html" class="block">
                <img src="${logoUrl}" alt="World Of Numeric Logo" class="h-10 transition-transform duration-300 hover:scale-105">
              </a>
            </div>

              <p class="text-gray-300 mb-6 leading-relaxed text-base">
                <span class="text-won-teal font-semibold">On ne suit pas le changement.</span> <br> On le crée. <br>
                <span class="text-won-pink font-semibold">IA, digital, formation</span> votre avenir commence ici.
              </p>



            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6 p-4 bg-gradient-to-r from-gray-800/30 to-gray-700/30 rounded-xl backdrop-blur-sm border border-gray-600/30">
              <div class="text-center">
                <div class="text-xl font-bold text-won-teal mb-1">150+</div>
                <div class="text-xs text-gray-400">Entreprises</div>
              </div>
              <div class="text-center sm:border-x border-gray-600/30">
                <div class="text-xl font-bold text-won-pink mb-1">500+</div>
                <div class="text-xs text-gray-400">Formations</div>
              </div>
              <div class="text-center">
                <div class="text-xl font-bold text-won-blue mb-1">95%</div>
                <div class="text-xs text-gray-400">Satisfaction</div>
              </div>
            </div>


            <div class="flex items-center space-x-4">
              <span class="text-sm text-gray-400 mr-2">Suivez-nous :</span>
              <div class="flex space-x-3">
              <a href="https://x.com/worldofnumeric" target="_blank" rel="noopener noreferrer" aria-label="World Of Numeric sur X (Twitter)" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center border border-gray-600/30 hover:border-won-pink/50 transition-all duration-300 hover:shadow-lg hover:shadow-won-pink/20 hover:-translate-y-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-200 group-hover:text-won-pink transition-colors duration-300" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2h3.86l-8.4 9.607L23 22h-7.156l-5.59-6.524L4.4 22H.54l8.793-10.053L1 2h7.156l5.237 6.114L18.244 2Zm-2.18 18.4h2.3L7.747 3.6H5.4l10.664 16.8Z" />
                  </svg>
                </a>
                <a href="https://facebook.com/worldofnumeric" target="_blank" rel="noopener noreferrer" aria-label="World Of Numeric sur Facebook" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center border border-gray-600/30 hover:border-won-blue/50 transition-all duration-300 hover:shadow-lg hover:shadow-won-blue/20 hover:-translate-y-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-200 group-hover:text-won-blue transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/>
                  </svg>
                </a>
                <a href="https://linkedin.com/company/worldofnumeric" target="_blank" rel="noopener noreferrer" aria-label="World Of Numeric sur LinkedIn" class="group relative w-10 h-10 rounded-xl bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center border border-gray-600/30 hover:border-won-orange/50 transition-all duration-300 hover:shadow-lg hover:shadow-won-orange/20 hover:-translate-y-1">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-200 group-hover:text-won-orange transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M4.98 3.5c0 1.381-1.11 2.5-2.48 2.5s-2.48-1.119-2.48-2.5c0-1.38 1.11-2.5 2.48-2.5s2.48 1.12 2.48 2.5zm.02 4.5h-5v16h5v-16zm7.982 0h-4.968v16h4.969v-8.399c0-4.67 6.029-5.052 6.029 0v8.399h4.988v-10.131c0-7.88-8.922-7.593-11.018-3.714v-2.155z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 lg:gap-16 w-full lg:w-auto">

            <div class="lg:ml-8">
              <div class="flex items-center mb-6 footer-title-container">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-won-teal to-won-emerald flex items-center justify-center mr-3 flex-shrink-0 footer-icon-container">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold text-white !border-l-0 !border-left-0" style="border-left: none !important; padding-left: 0 !important;">Liens rapides</h3>
              </div>
              <ul class="space-y-4 list-none !list-none ml-0 pl-0" style="list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">
                <li>
                  <a href="index.html" class="text-gray-300 hover:text-won-teal transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-teal group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Accueil
                  </a>
                </li>
                <li>
                  <a href="index.html#services" class="text-gray-300 hover:text-won-teal transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-teal group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Services
                  </a>
                </li>
                <li>
                  <a href="blog.html" class="text-gray-300 hover:text-won-blue transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-blue group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Blog
                  </a>
                </li>
                <li>
                  <a href="contact.html" class="text-gray-300 hover:text-won-teal transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-teal group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <div class="flex items-center mb-6 footer-title-container">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-won-blue to-won-pink flex items-center justify-center mr-3 flex-shrink-0 footer-icon-container">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold text-white !border-l-0 !border-left-0" style="border-left: none !important; padding-left: 0 !important;">Nos services</h3>
              </div>
              <ul class="space-y-4 list-none !list-none ml-0 pl-0" style="list-style: none !important; margin-left: 0 !important; padding-left: 0 !important;">
                <li>
                  <a href="index.html#services" class="text-gray-300 hover:text-won-blue transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-blue group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Accompagnement IA
                  </a>
                </li>
                <li>
                  <a href="index.html#formations" class="text-gray-300 hover:text-won-blue transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-blue group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Formation IA
                  </a>
                </li>
                <li>
                  <a href="index.html#services" class="text-gray-300 hover:text-won-blue transition-all duration-300 flex items-center group/link text-sm">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 text-gray-400 group-hover/link:text-won-blue group-hover/link:translate-x-1 transition-all duration-300 mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M9 5l7 7-7 7" />
                    </svg>
                    Transformation Digitale
                  </a>
                </li>

              </ul>
            </div>


            <div class="md:col-span-2 lg:col-span-2">
              <div class="flex items-center mb-6 footer-title-container">
                <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-won-pink to-won-orange flex items-center justify-center mr-3 flex-shrink-0 footer-icon-container">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 class="text-lg font-bold text-white !border-l-0 !border-left-0" style="border-left: none !important; padding-left: 0 !important;">Restez informé</h3>
              </div>

              <div class="p-8 rounded-2xl bg-gradient-to-br from-gray-800/40 to-gray-700/40 backdrop-blur-sm border border-gray-600/30">
                <p class="text-gray-300 mb-6 text-sm leading-relaxed">
                  Recevez nos dernières actualités et offres exclusives sur
                  <span class="text-won-pink font-semibold">l'IA</span> et la
                  <span class="text-won-teal font-semibold">transformation digitale</span>.
                </p>

                <form class="space-y-5" id="newsletter-form">
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input type="text" name="firstName" placeholder="Prénom"
                      class="bg-gray-800/60 border border-gray-600/50 rounded-xl px-4 py-3 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-won-blue/40 focus:border-won-blue/40 transition-all duration-300 text-sm" />
                    <input type="text" name="lastName" placeholder="Nom"
                      class="bg-gray-800/60 border border-gray-600/50 rounded-xl px-4 py-3 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-won-blue/40 focus:border-won-blue/40 transition-all duration-300 text-sm" />
                    <input type="text" name="company" placeholder="Entreprise"
                      class="bg-gray-800/60 border border-gray-600/50 rounded-xl px-4 py-3 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-won-blue/40 focus:border-won-blue/40 transition-all duration-300 text-sm" />
                  </div>
                  <div class="relative">
                    <input
                      type="email"
                      name="email"
                      placeholder="votre@email.com"
                      required
                      class="w-full bg-gray-800/60 border border-gray-600/50 rounded-xl px-5 py-4 text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-won-pink/50 focus:border-won-pink/50 transition-all duration-300 text-sm"
                    >
                    <div class="absolute inset-y-0 right-0 flex items-center pr-4">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                      </svg>
                    </div>
                  </div>

                  <button
                    type="submit"
                    class="w-full bg-gradient-to-r from-won-pink to-won-orange hover:from-won-pink/80 hover:to-won-orange/80 text-white px-6 py-4 rounded-full transition-all duration-300 shadow-lg shadow-won-pink/20 hover:shadow-won-pink/30 hover:-translate-y-0.5 text-sm font-semibold flex items-center justify-center"
                  >
                    S'inscrire maintenant
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </button>
                </form>

                <p class="text-gray-400 text-xs mt-5 text-center">
                  🔒 Vos données sont sécurisées -
                  <a href="confidentialite.html" class="text-won-teal hover:text-won-teal/80 transition-colors">Politique de confidentialité</a>
                </p>
              </div>
            </div>
          </div>
        </div>



          <div class="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <div class="flex items-center space-x-4">
              <p class="text-gray-400 text-sm">
                &copy; 2025
                <span class="text-won-teal font-semibold">World Of Numeric</span>
                . Tous droits réservés.
              </p>
            </div>

            <div class="flex flex-wrap justify-center lg:justify-end items-center space-x-6">
              <a href="confidentialite.html" class="text-gray-400 hover:text-won-pink transition-all duration-300 text-sm group flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1.5 group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Politique de confidentialité
              </a>
              <a href="mentions-legales.html" class="text-gray-400 hover:text-won-blue transition-all duration-300 text-sm group flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1.5 group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Mentions légales
              </a>
              <a href="cgu.html" class="text-gray-400 hover:text-won-teal transition-all duration-300 text-sm group flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1.5 group-hover:rotate-12 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                CGU
              </a>
              <button id="cookie-preferences-btn" class="text-gray-200 hover:text-won-orange transition-all duration-300 text-sm group flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 mr-1.5 group-hover:rotate-12 transition-transform duration-300 text-gray-200 group-hover:text-won-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                🍪 Paramètres des cookies
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private addBackgroundEffects(): void {
    // Effet parallax subtil uniquement pour les particules
    const particles = this.element.querySelectorAll('[class*="animate-"]');

    // Animation des particules avec délais aléatoires
    particles.forEach((particle, index) => {
      const delay = Math.random() * 2000;
      setTimeout(() => {
        (particle as HTMLElement).style.animationDelay = `${delay}ms`;
      }, index * 100);
    });

    // Effet parallax très subtil pour les particules seulement
    const handleScroll = () => {
      const scrolled = window.pageYOffset;
      const rate = scrolled * -0.2; // Réduit l'effet

      const backgroundParticles = this.element.querySelectorAll('.absolute.inset-0 > div');
      backgroundParticles.forEach((el, index) => {
        const element = el as HTMLElement;
        const speed = (index + 1) * 0.05; // Vitesse réduite
        element.style.transform = `translateY(${rate * speed}px)`;
      });
    };

    // Débounce pour optimiser les performances
    let ticking = false;
    const handleScrollOptimized = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScrollOptimized, { passive: true });

    // Nettoyage lors de la destruction du composant
    this.element.addEventListener('DOMNodeRemoved', () => {
      window.removeEventListener('scroll', handleScrollOptimized);
    });
  }

  private setupEventListeners(): void {
    // Gestion du bouton Paramètres des cookies
    const cookiePreferencesBtn = this.element.querySelector('#cookie-preferences-btn');
    if (cookiePreferencesBtn) {
      cookiePreferencesBtn.addEventListener('click', () => {
        CookieManager.showPreferencesModal();
      });
    }

    // Attache notre gestionnaire de façon robuste (indépendant de NewsletterManager)
    const newsletterForm = this.element.querySelector('#newsletter-form');
    if (newsletterForm) {
      const emailInput = newsletterForm.querySelector('input[type="email"]');
      const submitButton = newsletterForm.querySelector('button[type="submit"]');
      if (emailInput && submitButton) {
        if (!(newsletterForm as HTMLElement).dataset.newsletterInitialized) {
          this.initializeNewsletterFallback(newsletterForm, emailInput, submitButton);
        }
      }
    } else {
      console.warn('❌ Formulaire newsletter non trouvé dans le footer');
    }

    // Animation hover légère pour les liens sociaux uniquement
    const socialLinks = this.element.querySelectorAll('.group');
    socialLinks.forEach(link => {
      link.addEventListener('mouseenter', () => {
        link.classList.add('scale-105');
      });

      link.addEventListener('mouseleave', () => {
        link.classList.remove('scale-105');
      });
    });
  }

  private initializeNewsletterFallback(form: Element, emailInput: Element, submitButton: Element): void {
    console.log('🔧 Initialisation fallback newsletter footer');

    // Marquer comme initialisé
    (form as HTMLElement).dataset.newsletterInitialized = 'true';

    form.addEventListener('submit', async (e) => {
      e.preventDefault();

      const email = (emailInput as HTMLInputElement).value.trim();
      const firstName = (form.querySelector('input[name="firstName"]') as HTMLInputElement)?.value?.trim() || '';
      const lastName = (form.querySelector('input[name="lastName"]') as HTMLInputElement)?.value?.trim() || '';
      const company = (form.querySelector('input[name="company"]') as HTMLInputElement)?.value?.trim() || '';

      if (!email) {
        this.showNotification('Veuillez saisir une adresse email valide', 'error');
        return;
      }

      console.log('📧 Tentative d\'inscription newsletter:', email);

      // Animation bouton
      const originalText = submitButton.innerHTML;
      (submitButton as HTMLButtonElement).disabled = true;
      submitButton.innerHTML = `
        <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Inscription...
      `;

      try {
        await this.submitNewsletterFooterData({ email, firstName, lastName, company });

        this.showNotification('✅ Inscription réussie ! Merci de votre confiance', 'success');
        (emailInput as HTMLInputElement).value = '';
        const extraInputs = form.querySelectorAll('input[name="firstName"], input[name="lastName"], input[name="company"]');
        extraInputs.forEach((el: Element) => ((el as HTMLInputElement).value = ''));

      } catch (error) {
        console.error('❌ Erreur inscription newsletter:', error);
        this.showNotification(`❌ Erreur: ${(error as Error).message}`, 'error');
      } finally {
        // Restaurer le bouton
        (submitButton as HTMLButtonElement).disabled = false;
        submitButton.innerHTML = originalText;
      }
    });
  }

  private async submitNewsletterFooter(email: string): Promise<void> {
    console.log('🚀 Soumission newsletter footer pour:', email);

    // Essayer d'abord l'API World of Numeric
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

    try {
      const response = await fetch(`${apiUrl}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          source: 'Footer Site Web'
        })
      });

      console.log(`📊 API Response status: ${response.status}`);

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }

      const result = await response.text();
      console.log('✅ API Success:', result);

    } catch (apiError) {
      console.warn('⚠️ API World of Numeric échouée, tentative Brevo fallback:', apiError);

      // Fallback vers Brevo
      await this.submitToBrevo(email);
    }
  }

  // Nouvelle méthode qui envoie toutes les données au backend
  private async submitNewsletterFooterData(data: { email: string; firstName?: string; lastName?: string; company?: string; }): Promise<void> {
    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3002';

    try {
      const response = await fetch(`${apiUrl}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: data.email,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          company: data.company || '',
          source: 'Footer Site Web'
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status} ${response.statusText}`);
      }
    } catch (e) {
      console.warn('⚠️ API newsletter indisponible, fallback Brevo…');
      await this.submitToBrevoWithDetails(data);
    }
  }

  private async submitToBrevoWithDetails(data: { email: string; firstName?: string; lastName?: string; company?: string; }) {
    const apiKey = import.meta.env.VITE_BREVO_API_KEY;
    const listId = parseInt(import.meta.env.VITE_BREVO_LIST_ID) || 1;
    if (!apiKey) throw new Error('Configuration Brevo manquante');

    const payload = {
      email: data.email,
      listIds: [listId],
      attributes: {
        PRENOM: data.firstName || undefined,
        NOM: data.lastName || undefined,
        NOM_ENTREPRISE: data.company || undefined,
        SUBSCRIPTION_DATE: new Date().toISOString().split('T')[0]
      },
      updateEnabled: true
    } as any;

    const response = await fetch('https://api.brevo.com/v3/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'api-key': apiKey },
      body: JSON.stringify(payload)
    });

    if (!response.ok && response.status !== 204) {
      let msg = `${response.status} ${response.statusText}`;
      try { const j = await response.json(); msg = j.message || msg; } catch {}
      throw new Error(`Brevo: ${msg}`);
    }
  }

  private async submitToBrevo(email: string): Promise<void> {
    const apiKey = import.meta.env.VITE_BREVO_API_KEY;
    const listId = parseInt(import.meta.env.VITE_BREVO_LIST_ID) || 1;

    if (!apiKey) {
      throw new Error('Configuration Brevo manquante');
    }

    const data = {
      email: email,
      listIds: [listId],
      attributes: {
        SUBSCRIPTION_DATE: new Date().toISOString().split('T')[0]
      },
      updateEnabled: true
    };

    const response = await fetch('https://api.brevo.com/v3/contacts', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey
      },
      body: JSON.stringify(data)
    });

    if (response.status === 201) {
      // Contact créé avec succès
      const result = await response.json();
      console.log('✅ Contact Brevo créé:', result);
    } else if (response.status === 204) {
      // Contact mis à jour (email déjà existant)
      console.log('✅ Contact Brevo mis à jour (email déjà existant)');
    } else if (response.status === 400) {
      // Vérifier si c'est un doublon
      try {
        const error = await response.json();
        if (error.code === 'duplicate_parameter') {
          console.log('✅ Email déjà inscrit à la newsletter');
          return; // Considéré comme un succès
        } else {
          throw new Error(`Erreur Brevo: ${error.message || JSON.stringify(error)}`);
        }
      } catch (parseError) {
        throw new Error(`Erreur Brevo: ${response.status} ${response.statusText}`);
      }
    } else {
      // Autres erreurs
      try {
        const error = await response.json();
        throw new Error(`Erreur Brevo: ${error.message || response.statusText}`);
      } catch (parseError) {
        throw new Error(`Erreur Brevo: ${response.status} ${response.statusText}`);
      }
    }

    console.log('✅ Brevo newsletter inscription réussie');
  }

  private showNotification(message: string, type: 'success' | 'error'): void {
    const notification = document.createElement('div');
    notification.className = `
      fixed bottom-4 right-4 z-50 p-4 rounded-xl shadow-lg transition-all duration-500 transform translate-x-full max-w-sm
      ${type === 'success'
        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white'
        : 'bg-gradient-to-r from-red-500 to-pink-600 text-white'
      }
    `;

    notification.innerHTML = `
      <div class="flex items-center space-x-3">
        <div class="flex-shrink-0">
          ${type === 'success'
            ? `<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
               </svg>`
            : `<svg class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                 <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
               </svg>`
          }
        </div>
        <div class="text-sm font-medium">${message}</div>
        <button class="flex-shrink-0 ml-4 hover:opacity-70 transition-opacity">
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    // Animation d'entrée
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);

    // Gestion de la fermeture
    const closeBtn = notification.querySelector('button');
    closeBtn?.addEventListener('click', () => {
      notification.classList.add('translate-x-full', 'opacity-0');
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 500);
    });

    // Auto-suppression après 5 secondes
    setTimeout(() => {
      if (notification.parentNode) {
        notification.classList.add('translate-x-full', 'opacity-0');
        setTimeout(() => {
          if (notification.parentNode) {
            notification.parentNode.removeChild(notification);
          }
        }, 500);
      }
    }, 5000);
  }

  public getElement(): HTMLElement {
    return this.element;
  }
}
